import { type FC, type ReactNode, useCallback, useState } from 'react';

import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import cn from 'classnames';

import { POPOVER_PANEL_CLASSES } from '../constants';
import { ToolbarButton } from '../toolbar/ToolbarButton';

interface PanelPopoverProps {
  triggerLabel: string;
  triggerIcon: ReactNode;
  panelClassName?: string;
  disabled?: boolean;
  /** Controlled open state. Omit to let the popover manage its own (media, emoji, posts). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: (ctx: { close: () => void }) => ReactNode;
}

export const PanelPopover: FC<PanelPopoverProps> = ({
  triggerLabel,
  triggerIcon,
  panelClassName,
  disabled,
  open: controlledOpen,
  onOpenChange,
  children,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const interactions = useInteractions([useClick(floating.context), useDismiss(floating.context)]);

  const close = useCallback(() => setOpen(false), [setOpen]);

  return (
    <>
      <ToolbarButton
        label={triggerLabel}
        icon={triggerIcon}
        active={open}
        disabled={disabled}
        buttonRef={floating.refs.setReference}
        buttonProps={interactions.getReferenceProps()}
      />
      {open && (
        <FloatingPortal>
          <div
            ref={floating.refs.setFloating}
            style={floating.floatingStyles}
            className={cn(POPOVER_PANEL_CLASSES, 'z-[100] flex flex-col', panelClassName)}
            {...interactions.getFloatingProps()}
          >
            {children({ close })}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
